import { useMemo } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import {
  queryKeys,
  getItemInstances,
  type ItemWithRelations,
  type ItemInstance,
} from '@pantry/api-client';
import { useMealPrepStore } from '../stores/meal-prep-store';

/**
 * Live selected item data combining item and instance.
 */
export interface LiveSelectedItem {
  item: ItemWithRelations;
  instance: ItemInstance;
}

/**
 * Hook that returns live item/instance data for meal prep selections.
 * Looks up data from React Query cache, which is always fresh after invalidation.
 *
 * This solves the stale data problem where Zustand was storing point-in-time
 * snapshots that didn't update after bulk operations.
 */
export function useMealPrepSelection(householdId: string) {
  const queryClient = useQueryClient();
  const { getSelectedInstanceIds, getItemIdForInstance, getSelectionCount } = useMealPrepStore();

  const selectedInstanceIds = getSelectedInstanceIds();

  // Get unique item IDs for the selected instances
  const uniqueItemIds = useMemo(() => {
    const ids = new Set<string>();
    for (const instanceId of selectedInstanceIds) {
      const itemId = getItemIdForInstance(instanceId);
      if (itemId) ids.add(itemId);
    }
    return [...ids];
  }, [selectedInstanceIds, getItemIdForInstance]);

  // Read items from cache using prefix matching
  // meal-prep.tsx may cache with different searchParams, so we match any items list query
  const itemsQueries = queryClient.getQueriesData<ItemWithRelations[]>({
    queryKey: ['items', householdId, 'list'],
    exact: false,
  });
  const itemsFromCache = itemsQueries[0]?.[1];

  // Fetch instances for each unique item
  // These are likely already cached from ItemCard components
  const instanceQueries = useQueries({
    queries: uniqueItemIds.map((itemId) => ({
      queryKey: queryKeys.items.instances(householdId, itemId),
      queryFn: () => getItemInstances(householdId, itemId),
      staleTime: 2 * 60 * 1000, // 2 minutes
      enabled: !!householdId && !!itemId,
    })),
  });

  // Build map of itemId -> instances
  const instancesByItemId = useMemo(() => {
    const map = new Map<string, ItemInstance[]>();
    uniqueItemIds.forEach((itemId, index) => {
      const query = instanceQueries[index];
      if (query.data) {
        map.set(itemId, query.data);
      }
    });
    return map;
  }, [uniqueItemIds, instanceQueries]);

  // Map selected instance IDs to live data
  const selectedItems = useMemo((): LiveSelectedItem[] => {
    if (!itemsFromCache) return [];

    return selectedInstanceIds
      .map((instanceId) => {
        const itemId = getItemIdForInstance(instanceId);
        if (!itemId) return null;

        const item = itemsFromCache.find((i) => i.id === itemId);
        if (!item) return null;

        const instances = instancesByItemId.get(itemId);
        const instance = instances?.find((i) => i.id === instanceId);
        if (!instance) return null;

        return { item, instance };
      })
      .filter((x): x is LiveSelectedItem => x !== null);
  }, [selectedInstanceIds, itemsFromCache, instancesByItemId, getItemIdForInstance]);

  const isLoading = instanceQueries.some((q) => q.isLoading);

  return {
    selectedItems,
    selectionCount: getSelectionCount(),
    isLoading,
  };
}
